import { createDisplayNumberFormat } from '../../utils/displayNumberFormat';
import { CHART_BAR_GREEN } from './chartFormatting';
import { NmSegmentControl } from '../NmSegmentControl';
import { ChartUnitValue } from './ChartUnitValue';
import './ChartPowerProfile.css';
import { MobileSheet } from '../MobileSheet';
import { powerProfileRanges } from './powerProfileRanges';
/**
 * Power Profile — vertical grouped bars with Coggan category zones (Ai-Tempo PowerProfile.tsx).
 * Plot area always uses the Android light theme; parent card follows web shell.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PowerProfileBar, PowerProfileGroup, PowerProfileZone } from './types';
import { formatApiDateCompact } from '../../utils/calendarPickerLocale';
import { displayDigits } from '../../utils/displayDigits';

const MAX_VALUE = 100;
const DEFAULT_CHART_HEIGHT = 280;
const LABEL_HEIGHT = 50;
const GROUP_GAP = 14;
const BAR_GAP = 5;
/** Normalized SVG width — stretched to 100% of the card via preserveAspectRatio="none". */
const PLOT_VIEW_WIDTH = 1000;

/** Android PowerProfile light plot palette — fixed regardless of app theme. */
const PLOT_TEXT = 'var(--power-profile-text, #1A1F2B)';
const PLOT_TEXT_SUBTLE = 'var(--power-profile-muted, #7B8796)';
const PLOT_GRID = 'var(--power-profile-grid, rgba(163,177,198,0.40))';
const PLOT_EMPTY_OVERLAY = 'var(--power-profile-empty-bg, rgba(232,236,242,0.88))';

const ZONE_BOUNDS = [0, 15, 27, 38, 50, 64, 76, 87, 100] as const;

function ltrValue(value: string | number): string {
  return `\u2066${displayDigits(value)}\u2069`;
}

function zoneLabelBottom(zone: PowerProfileZone, chartHeight: number): number {
  const mid = (zone.min + zone.max) / 2;
  return (mid / MAX_VALUE) * chartHeight;
}

function barHeight(value: number, chartHeight: number): number {
  const safe = Number.isFinite(value) ? value : 0;
  return Math.max(1, (safe / MAX_VALUE) * chartHeight);
}

export interface PowerProfilePopup {
  show: boolean;
  barKey?: string;
  title?: string;
  weight?: number;
  power?: number;
  wattsPerKg?: number;
  startDate?: string;
  endDate?: string;
  workoutId?: number;
}

export interface ChartPowerProfileProps {
  data: PowerProfileGroup[];
  /** Inside ChartsPage card — no extra outer chrome. */
  embedded?: boolean;
  height?: number;
  style?: React.CSSProperties;
  onWorkoutClick?: (workoutId: number) => void;
  onSettingsPress?: () => void;
}

export function ChartPowerProfile({
  data,
  embedded = false,
  height = DEFAULT_CHART_HEIGHT,
  style,
  onWorkoutClick,
  onSettingsPress,
}: ChartPowerProfileProps) {
  const { t, i18n } = useTranslation();
  const [popup, setPopup] = useState<PowerProfilePopup | null>(null);
  useEffect(() => {
    if (!popup?.show) return;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setPopup(null); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [popup?.show]);

  const [showReference, setShowReference] = useState(false);
  const [referenceInterval, setReferenceInterval] = useState('power5sec');
  const referenceGroup = data.find(g => g.intervalKey === referenceInterval) ?? data.find(g => g.intervalKey !== 'ftp');
  const referenceRanges = referenceGroup?.referenceSex ? powerProfileRanges[referenceGroup.referenceSex][referenceGroup.intervalKey as keyof typeof powerProfileRanges.male] : undefined;
  const categoryKeys = ['Untrained', 'Fair', 'Moderate', 'Good', 'Very Good', 'Excellent', 'Exceptional', 'World Class'];

  const yAxisWidth = showReference ? 112 : 82;
  const number = (value: number) => createDisplayNumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value);
  const selectedBar = referenceGroup?.bars.reduce<PowerProfileBar | undefined>((best, bar) => !best || bar.wkg > best.wkg ? bar : best, undefined);
  const selectedCategory = selectedBar && referenceRanges ? [...referenceRanges].sort((a, b) => b.min - a.min).find(range => selectedBar.wkg >= range.min)?.category : selectedBar?.category;
  const zoneIndex = selectedCategory ? categoryKeys.indexOf(selectedCategory) : -1;
  const chartHeight = height;

  const zones = useMemo<PowerProfileZone[]>(() => [
    { label: t('charts.powerProfile.zones.untrained'), min: 0, max: 15 },
    { label: t('charts.powerProfile.zones.fair'), min: 15, max: 27 },
    { label: t('charts.powerProfile.zones.moderate'), min: 27, max: 38 },
    { label: t('charts.powerProfile.zones.good'), min: 38, max: 50 },
    { label: t('charts.powerProfile.zones.veryGood'), min: 50, max: 64 },
    { label: t('charts.powerProfile.zones.excellent'), min: 64, max: 76 },
    { label: t('charts.powerProfile.zones.exceptional'), min: 76, max: 87 },
    { label: t('charts.powerProfile.zones.worldClass'), min: 87, max: 100 },
  ], [t]);

  const groups = useMemo(() => {
    return data
      .filter((group) => group.intervalKey !== 'ftp')
      .map((group) => {
      const c1 = 'var(--brand-primary)';
      const c2 = c1;
      return {
        ...group,
        color: c1,
        color2: c2,
        label: t(`charts.powerProfile.intervals.${group.intervalKey}`, { defaultValue: displayDigits(group.interval) }),
        bars: group.bars.map((bar) => ({
          ...bar,
          color: bar.color ?? c1,
          color2: bar.color2 ?? c2,
        })),
      };
    });
  }, [data, t, i18n.resolvedLanguage, i18n.language]);

  const hasData = groups.length > 0;
  const plotWidth = PLOT_VIEW_WIDTH;
  const totalHeight = chartHeight + LABEL_HEIGHT;

  const handleBarPress = useCallback((groupKey: string, bar: PowerProfileBar, barIndex: number) => {
    setReferenceInterval(groupKey);
    const barKey = `${groupKey}-${barIndex}`;
    if (popup?.show && popup.barKey === barKey) {
      setPopup(null);
      return;
    }
    setPopup({
      show: true,
      barKey,
      title: groupKey,
      weight: bar.weight,
      power: bar.power,
      wattsPerKg: bar.wkg,
      startDate: bar.startDate,
      endDate: bar.endDate,
      workoutId: bar.workoutId,
    });
  }, [popup]);

  const outerStyle: React.CSSProperties = { position: 'relative', width: '100%', ...style };
  const plotStyle: React.CSSProperties = { background: 'transparent', overflow: 'visible' };

  return (
    <div className={`pp-chart-card${embedded ? " pp-chart-card--embedded" : ""}`} style={outerStyle}>
      <div className="pp-profile-reading">
        <div><span>{referenceGroup && t(`charts.powerProfile.intervals.${referenceGroup.intervalKey}`)}</span>
          <strong>{selectedBar ? <ChartUnitValue value={number(selectedBar.wkg)} unit={t('charts.units.wkg')} /> : '—'}</strong></div>
        <div className="pp-profile-reading__context"><span>{referenceGroup?.referenceSex && t(`charts.powerProfile.reference.${referenceGroup.referenceSex}`)}</span>
          <b>{zoneIndex >= 0 ? zones[zoneIndex].label : selectedBar?.category}</b></div>
      </div>
      {referenceRanges && <label className="pp-profile-reference-switch" title={t('charts.powerProfile.reference.note')}>
        <span>{t('charts.powerProfile.reference.title')}</span>
        <input type="checkbox" role="switch" checked={showReference} onChange={event => setShowReference(event.target.checked)} aria-label={t('charts.powerProfile.reference.title')} />
      </label>}
      <div
        className="pp-chart-plot ltr-island"
        dir="ltr"
        style={{ display: 'flex', flexDirection: 'row', width: '100%', boxSizing: 'border-box', height: totalHeight, ...plotStyle, overflowX: 'auto' }}
      >
        {/* Y-axis zone labels — always on the physical left of the plot. */}
        <div
          className="pp-chart-y-axis"
          style={{
            position: 'relative',
            width: yAxisWidth,
            height: chartHeight,
            flexShrink: 0,
          }}
        >
          {zones.map((zone, index) => (
            <span
              key={zone.label}
              className="pp-chart-y-label"
              style={{
                position: 'absolute',
                left: 0,
                right: 14,
                bottom: zoneLabelBottom(zone, chartHeight),
                transform: 'translateY(50%)',
                fontSize: 'var(--chart-axis-label-size)',
                lineHeight: 1.2,
                color: PLOT_TEXT_SUBTLE,
                textAlign: 'center',
                letterSpacing: '-0.2px',
                direction: i18n.dir() === 'rtl' ? 'rtl' : 'ltr',
                unicodeBidi: 'isolate',
                fontFamily: i18n.dir() === 'rtl'
                  ? 'Vazir, Vazirmatn, var(--font-body), sans-serif'
                  : 'var(--font-body)',
                fontWeight: 600,
                pointerEvents: 'none',
                maxWidth: yAxisWidth - 14,
              }}
            >
              {zone.label}
              {showReference && referenceRanges && <span className="pp-profile-axis-range" dir="ltr">{(() => {
                const range = referenceRanges.find(item => item.category === categoryKeys[index]);
                return range ? `${number(range.min)} – ${number(range.max)}` : '';
              })()}</span>}

            </span>
          ))}
        </div>

        {/* Chart plot — bars + x-axis labels. */}
        <div className="pp-chart-plot-area" style={{ position: 'relative', flex: 1, minWidth: Math.max(240, groups.length * 160), overflow: 'visible' }}>
          <svg
            viewBox={`0 0 ${plotWidth} ${chartHeight}`}
            preserveAspectRatio="none"
            style={{ width: '100%', height: chartHeight, display: 'block' }}
            role="img"
            aria-label={t('charts.powerProfile.ariaChart')}
          >


            {ZONE_BOUNDS.map((bound) => {
              const y = chartHeight - (bound / MAX_VALUE) * chartHeight;
              return (
                <line
                  key={bound}
                  x1={0}
                  y1={y}
                  x2={plotWidth}
                  y2={y}
                  stroke={PLOT_GRID}
                  strokeWidth={1}
                  strokeDasharray="4 4"
                />
              );
            })}

            <g>
              {groups.map((group, gi) => {
                const groupCount = Math.max(1, groups.length);
                const groupW = plotWidth / groupCount;
                const slotX = gi * groupW + GROUP_GAP / 2;
                const innerW = groupW - GROUP_GAP;
                const barCount = Math.max(1, group.bars.length);
                const slotBarW = (innerW - BAR_GAP * Math.max(0, barCount - 1)) / barCount;
                const maxBarW = Math.min(slotBarW, innerW * 0.72);

                return group.bars.map((bar, bi) => {
                  const h = barHeight(bar.normalized, chartHeight);
                  const totalBarsW = barCount * maxBarW + BAR_GAP * Math.max(0, barCount - 1);
                  const groupCenter = slotX + innerW / 2;
                  const x = groupCenter - totalBarsW / 2 + bi * (maxBarW + BAR_GAP);
                  const y = chartHeight - h;
                  const barW = Math.max(maxBarW, 6);

                  return (
                    <g
                      key={`${group.intervalKey}-${bi}`}
                      className="pp-chart-bar"
                      onClick={() => handleBarPress(group.intervalKey, bar, bi)}
                      role="button" tabIndex={0} aria-label={`${group.label}: ${number(bar.wkg)} ${t('charts.units.wkg')}`}
                      onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleBarPress(group.intervalKey, bar, bi); } }}
                      style={{ cursor: 'pointer' }}
                    >
                      <title>{`${group.label} · ${number(bar.wkg)} ${t('charts.units.wkg')}`}</title>
                      <rect
                        x={x}
                        y={0}
                        width={barW}
                        height={chartHeight}
                        fill="transparent"
                      />
                      <rect
                        x={x}
                        y={y}
                        width={barW}
                        height={h}
                        rx={10}
                        ry={10}
                        fill={referenceGroup?.intervalKey === group.intervalKey ? CHART_BAR_GREEN : 'var(--chart-bar-muted)'}
                        opacity={referenceGroup?.intervalKey === group.intervalKey ? 1 : .65}

                      />

                    </g>
                  );
                });
              })}
            </g>
          </svg>

          <div className="pp-profile-values" style={{ height: chartHeight }} aria-hidden="true">
            {groups.map(group => <div key={group.intervalKey}>{group.bars.length <= 3 && group.bars.map((bar, index) =>
              <bdi key={index} style={{ top: Math.max(0, chartHeight - barHeight(bar.normalized, chartHeight) - 26), left: `${(index + .5) / group.bars.length * 100}%` }}>{number(bar.wkg)}</bdi>
            )}</div>)}
          </div>
          {/* Keep duration tabs in the same physical order as their bar columns. */}
          <NmSegmentControl fullWidth className="pp-chart-period-tabs"
            ariaLabel={t('charts.powerProfile.referenceInterval')}
            value={referenceGroup?.intervalKey ?? referenceInterval}
            options={groups.map(group => ({ value: group.intervalKey, label: group.label }))}
            onChange={interval => { setReferenceInterval(interval); setPopup(null); }} />

          {/* Empty overlay */}
          {!hasData && (
            <div
              className="pp-chart-empty"
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: '0 16px',
                background: PLOT_EMPTY_OVERLAY,
                borderRadius: 8,
                zIndex: 20,
                textAlign: 'center',
              }}
            >
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: PLOT_TEXT, whiteSpace: 'pre-line' }}>
                {t('charts.powerProfile.empty')}
              </p>
              {onSettingsPress && (
                <button
                  type="button"
                  onClick={onSettingsPress}
                  aria-label={t('charts.powerProfile.openSettings')}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 8,
                    cursor: 'pointer',
                    color: PLOT_TEXT,
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Record details open below the chart in the shared bottom sheet. */}
          {popup?.show && (
            <MobileSheet open onClose={() => setPopup(null)} title={t('charts.powerProfile.ariaChart')} zIndex={15000}>
            <div dir={i18n.dir()} style={{ padding: 16, fontSize: 13, lineHeight: 1.9, color: 'var(--fg-1)', textAlign: 'start' }}>
              <button type="button" autoFocus onClick={() => setPopup(null)} aria-label={t('common.close')} style={{ display: 'block', marginInlineStart: 'auto', border: 0, borderRadius: 8, padding: '8px 12px', background: 'var(--surface-2)', color: 'var(--fg-1)', font: 'inherit', cursor: 'pointer' }}>{t('common.close')}</button>
              {popup.title ? (
                <div className="pp-chart-popup__line">{t(`charts.powerProfile.intervals.${popup.title}`)}</div>
              ) : null}
              <div className="pp-chart-popup__line">{t('charts.powerProfile.popup.weight', { value: ltrValue(popup.weight ?? '—') })}</div>
              <div className="pp-chart-popup__line">{t('charts.powerProfile.popup.power', { value: ltrValue(popup.power != null ? Math.round(Number(popup.power)) : '—') })}</div>
              <div className="pp-chart-popup__line">{t('charts.powerProfile.popup.wkg', { value: ltrValue(popup.wattsPerKg != null ? Math.round(Number(popup.wattsPerKg) * 10) / 10 : '—') })}</div>
              <div className="pp-chart-popup__line">{t('charts.powerProfile.popup.dateRange', {
                start: ltrValue(popup.startDate ? formatApiDateCompact(popup.startDate, i18n.language) : '—'),
                end: ltrValue(popup.endDate ? formatApiDateCompact(popup.endDate, i18n.language) : '—'),
              })}</div>
              {popup.workoutId && onWorkoutClick ? (
                <>
                  {'\n'}
                  <button
                    type="button"
                    onClick={() => { const workoutId = popup.workoutId!; setPopup(null); onWorkoutClick(workoutId); }}
                    style={{
                      marginTop: 6,
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      color: '#13A89E',
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    {t('charts.powerProfile.viewWorkout')}
                  </button>
                </>
              ) : null}
            </div>
            </MobileSheet>
          )}
        </div>
      </div>

    </div>
  );
}
